/**
 * @fileoverview Utility functions for processing company legal right composite entities.
 *
 * This module provides utilities to extract, merge, and deduplicate legal rights
 * from company legal right composite entities. It handles both person-based and
 * company-based legal right associations, creating a unified dictionary structure
 * that maps entity UUIDs to their associated legal rights.
 *
 * @author NK Development Team
 * @version 1.0.0
 */

import { TranslateService } from '@ngx-translate/core';
import { CompanyLegalRightCompositeEntity, LegalRightEntity } from '@nk/data-entities-lib';
import { arrayUnique } from '@nk/utils-lib';

/**
 * Dictionary mapping entity UUIDs (person or company) to their associated legal rights.
 *
 * @interface LegalRightDict
 * @description A key-value structure where:
 * - Key: Entity UUID (string) - either a person UUID or company UUID
 * - Value: Array of LegalRightEntity objects associated with that entity
 */
export interface LegalRightDict {
  [entityUuid: string]: LegalRightEntity[];
}

/**
 * Extracts legal rights from a single company legal right composite entity.
 *
 * @private
 * @param composite - The company legal right composite entity to process
 * @returns A dictionary mapping entity UUIDs to their legal rights
 *
 * @description This function processes both person and company legal right composites
 * from a single composite entity and groups the legal rights by entity UUID.
 * It handles both person-based and company-based legal right associations.
 */
function getLegalRightSubDict(composite: CompanyLegalRightCompositeEntity): LegalRightDict {
  let subDict: LegalRightDict = {};
  const legalRightComposites = [
    ...(composite.company_legal_right_person_composites || []),
    ...(composite.company_legal_right_company_composites || []),
  ];
  if (composite.legal_right && Array.isArray(legalRightComposites)) {
    subDict = legalRightComposites.reduce((dict: LegalRightDict, legalRightComposite) => {
      if ('person' in legalRightComposite) {
        if (!Array.isArray(dict[legalRightComposite.person.uuid])) {
          dict[legalRightComposite.person.uuid] = [];
        }
        dict[legalRightComposite.person.uuid].push(composite.legal_right);
      } else {
        if (!Array.isArray(dict[legalRightComposite.company.uuid ?? ''])) {
          dict[legalRightComposite.company.uuid ?? ''] = [];
        }
        dict[legalRightComposite.company.uuid ?? ''].push(composite.legal_right);
      }
      return dict;
    }, {});
  }
  return subDict;
}

/**
 * Merges two legal right dictionaries, combining legal rights for entities that exist in both.
 *
 * @private
 * @param originalDict - The original legal right dictionary
 * @param additionDict - The dictionary to merge into the original
 * @returns A new dictionary containing merged legal rights
 *
 * @description This function combines legal rights from two dictionaries:
 * - If an entity exists in both dictionaries, their legal rights are concatenated
 * - If an entity only exists in the addition dictionary, it's added as-is
 * - If the original dictionary is null/undefined, returns an empty object
 */
function mergeLegalRightDicts(originalDict: LegalRightDict, additionDict: LegalRightDict): LegalRightDict {
  if (!originalDict) {
    return {};
  }
  const res: LegalRightDict = { ...originalDict };
  for (const entityUuid of Object.keys(additionDict)) {
    if (entityUuid in originalDict) {
      res[entityUuid] = res[entityUuid].concat(additionDict[entityUuid]);
    } else {
      res[entityUuid] = additionDict[entityUuid];
    }
  }
  return res;
}

/**
 * Removes duplicate legal rights from each entity's legal rights array.
 *
 * @private
 * @param dict - The legal right dictionary to deduplicate
 * @returns A new dictionary with unique legal rights per entity
 *
 * @description This function ensures that each entity has only unique legal rights
 * by using the legal_right_key as the uniqueness identifier. Duplicate legal rights
 * with the same key are removed from each entity's array.
 */
function getUniqueLegalRights(dict: LegalRightDict): LegalRightDict {
  if (!dict) {
    return {};
  }
  const res: LegalRightDict = {};
  for (const entityUuid of Object.keys(dict)) {
    res[entityUuid] = arrayUnique(dict[entityUuid], (c) => c.legal_right_key);
  }
  return res;
}

/**
 * Creates a legal right dictionary from an array of company legal right composite entities.
 *
 * @param companyLegalRightComposites - Array of company legal right composite entities to process
 * @returns A dictionary mapping entity UUIDs (person or company) to their associated unique legal rights
 *
 * @description This is the main public function that:
 * 1. Processes each company legal right composite entity
 * 2. Extracts legal rights for both person and company entities
 * 3. Merges all legal rights into a single dictionary
 * 4. Removes duplicate legal rights based on legal_right_key
 *
 * @example
 * ```typescript
 * const composites: WithUuid<CompanyLegalRightCompositeEntity>[] = [...];
 * const legalRightsByEntity = getLegalRightDictByEntityUuid(composites);
 * // Result: { "person-uuid-1": [LegalRightEntity, ...], "company-uuid-1": [LegalRightEntity, ...] }
 * ```
 */
export function getLegalRightDictByEntityUuid(
  companyLegalRightComposites: CompanyLegalRightCompositeEntity[],
): LegalRightDict {
  if (!Array.isArray(companyLegalRightComposites)) {
    return {};
  }
  return companyLegalRightComposites.reduce((res: LegalRightDict, composite) => {
    const legalRightSubDict = getLegalRightSubDict(composite);
    res = mergeLegalRightDicts(res, legalRightSubDict);
    return getUniqueLegalRights(res);
  }, {});
}

function isLegalRightEntity(item: unknown): item is LegalRightEntity {
  return !!item && !!(item as LegalRightEntity).legal_right_key;
}

export function getLegalRightKeyTranslation(items: LegalRightEntity[], translateService: TranslateService): string {
  if (!(Array.isArray(items) && items.length)) {
    return '';
  }
  return items
    .map((i) => (isLegalRightEntity(i) ? i : null))
    .filter((i) => !!i)
    .map((i) => translateService.instant(`legal_right.${i.legal_right_key}`) as string)
    .join(', ');
}
